import type { ComplianceAlertDto } from 'korint-sdk';
import { Check, Square, SquareCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../components/ui/tooltip';
import { formatDateWithTime } from '../../lib/date-formatting';
import type { RawdataMatch } from './types';
import { getAccuracyTone, getAlertListKey, getAlertListTone } from './utils';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';
import { Badge } from '@folio/components/ui/badge';

export function AlertRow({
  alert,
  selected,
  onToggleSelect,
  onAcknowledge,
  isPending,
  canAcknowledge,
}: {
  alert: ComplianceAlertDto;
  selected: boolean;
  onToggleSelect: (id: string) => void;
  onAcknowledge: (alertId: string) => void;
  isPending: boolean;
  canAcknowledge: boolean;
}) {
  const { t } = useTranslation();
  const rawdata = (alert.data?.rawdata ?? []) as RawdataMatch[];
  const maxAccuracy = rawdata.reduce((max, r) => Math.max(max, r.accuracy ?? 0), 0);
  const matches = rawdata.filter(r => r.result?.name);
  const createdAtFormatted = formatDateWithTime(new Date(alert.createdAt));
  const acknowledgedAtFormatted = alert.acknowledgedAt
    ? formatDateWithTime(new Date(alert.acknowledgedAt))
    : null;

  return (
    <div className="border-b border-border/50 last:border-0">
      <Group align="start" gap={3} className="py-3 px-1">
        <div className="pt-0.5">
          {!alert.acknowledged ? (
            canAcknowledge ? (
              <button
                type="button"
                onClick={() => onToggleSelect(alert.id)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                {selected ? (
                  <SquareCheck className="h-4 w-4 text-primary" />
                ) : (
                  <Square className="h-4 w-4" />
                )}
              </button>
            ) : (
              <div className="h-4 w-4" />
            )
          ) : (
            <div className="h-4 w-4 flex items-center justify-center">
              <div className="h-2.5 w-2.5 rounded-full bg-green-500" />
            </div>
          )}
        </div>

        <Stack gap={1} className="flex-1 min-w-0">
          <Group wrap gap={2}>
            {maxAccuracy ? (
              <Badge variant="light" tone={getAccuracyTone(maxAccuracy)} size="sm">
                {maxAccuracy}%
              </Badge>
            ) : null}
            {alert.data?.lists?.map(list => {
              const key = getAlertListKey(list);
              return (
                <Badge key={list} variant="light" tone={getAlertListTone(list)} size="sm">
                  {key ? t(`compliance.alertLists.${key}`) : list}
                </Badge>
              );
            })}
            {matches.map((match, idx) => (
              <Text inline size="sm" weight="medium" key={`${match.result!.name}-${idx}`}>
                {match.result!.name}
              </Text>
            ))}
          </Group>

          <Group gap={3} className="text-xs text-muted-foreground">
            <span>{createdAtFormatted}</span>
            {alert.acknowledged && acknowledgedAtFormatted && (
              <>
                <span>&middot;</span>
                <span className="text-green-700">
                  {t('compliance.acknowledgedAt', { date: acknowledgedAtFormatted })}
                </span>
              </>
            )}
          </Group>
        </Stack>

        {canAcknowledge && !alert.acknowledged && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="shrink-0 h-8 w-8 p-0 hover:bg-green-50"
                onClick={() => onAcknowledge(alert.id)}
                disabled={isPending}
              >
                <Check className="h-4 w-4 text-green-600" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('compliance.actions.acknowledge')}</TooltipContent>
          </Tooltip>
        )}
      </Group>
    </div>
  );
}
